import { Card, CircularProgress, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import {
  formatRevenueExpenseCategory,
  normalizeRevenueExpenseCategory,
} from "./revenueExpenseCategories";
import { getRevenueExpenseCategoryLimits } from "./revenueExpensesApi";
import RevenueExpenseCategoryLimitModal from "./RevenueExpenseCategoryLimitModal";
import { useMaterialUIController } from 'context';
import { HIDDEN_CURRENCY_LABEL } from "utils/valueVisibility";


const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});
const DEFAULT_VISIBLE_LIMITS = 5;

function RevenueExpenseCategoryLimitsCard({ openSnackBar, valuesHidden }) {
  const [limits, setLimits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAllLimits, setShowAllLimits] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    category: "",
    maxValue: "",
    hasExistingLimit: false,
  });

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const displayCurrency = (value) =>
    valuesHidden ? HIDDEN_CURRENCY_LABEL : currencyFormatter.format(Number(value ?? 0));
  const totalMonthlyLimit = useMemo(
    () => limits.reduce((total, item) => total + Number(item.maxValue ?? 0), 0),
    [limits]
  );
  const visibleLimits = showAllLimits ? limits : limits.slice(0, DEFAULT_VISIBLE_LIMITS);
  const hiddenLimitsCount = Math.max(limits.length - DEFAULT_VISIBLE_LIMITS, 0);

  useEffect(() => {
    const loadCategoryLimits = async () => {
      setIsLoading(true);

      try {
        const data = await getRevenueExpenseCategoryLimits();
        setLimits(Array.isArray(data) ? data : []);
      } catch (error) {
        openSnackBar?.({ type: "error", message: `${error}` });
        setLimits([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCategoryLimits();
  }, [openSnackBar]);

  const openLimitModal = (categoryValue, currentLimitValue) => {
    const normalizedCategory = categoryValue?.trim();

    if (!normalizedCategory) {
      return;
    }

    setModalState({
      open: true,
      category: normalizedCategory,
      maxValue: currentLimitValue ?? "",
      hasExistingLimit: currentLimitValue != null,
    });
  };

  const openNewLimitModal = () => {
    setModalState({
      open: true,
      category: "",
      maxValue: "",
      hasExistingLimit: false,
    });
  };

  const handleModalSaved = (savedItem) => {
    const isNewLimit = !limits.some(
      (item) => normalizeRevenueExpenseCategory(item.category) === normalizeRevenueExpenseCategory(savedItem.category)
    );

    setLimits((previousState) => {
      const hasExistingItem = previousState.some(
        (item) => normalizeRevenueExpenseCategory(item.category) === normalizeRevenueExpenseCategory(savedItem.category)
      );

      if (hasExistingItem) {
        return previousState
          .map((item) =>
            normalizeRevenueExpenseCategory(item.category) === normalizeRevenueExpenseCategory(savedItem.category)
              ? savedItem
              : item
          )
          .sort((firstItem, secondItem) => firstItem.category.localeCompare(secondItem.category));
      }

      return [...previousState, savedItem].sort((firstItem, secondItem) =>
        firstItem.category.localeCompare(secondItem.category)
      );
    });

    if (isNewLimit) {
      setShowAllLimits(true);
    }
  };

  const handleModalDeleted = (deletedCategory) => {
    setLimits((previousState) =>
      previousState.filter(
        (item) => normalizeRevenueExpenseCategory(item.category) !== normalizeRevenueExpenseCategory(deletedCategory)
      )
    );
  };

  const summaryText = isLoading
    ? "Carregando limites..."
    : limits.length > 0
      ? `${limits.length} categoria${limits.length > 1 ? "s" : ""} configurada${limits.length > 1 ? "s" : ""} • Total mensal ${displayCurrency(totalMonthlyLimit)}`
      : "Nenhum limite configurado ainda.";

  return (
    <>
      <Card sx={{ height: "100%" }}>
        <MDBox
          pt={2}
          px={2}
          pb={2}
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          gap={1.5}
          lineHeight={1.25}
        >
          <MDBox>
            <MDTypography variant="h6" fontWeight="medium">
              Limites por categoria
            </MDTypography>
            <MDTypography variant="button" color="text">
              {summaryText}
            </MDTypography>
          </MDBox>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={openNewLimitModal}
            sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 170 }, gap: 0.75 }}
          >
            <AddIcon fontSize="small" />
            Adicionar limite
          </MDButton>
        </MDBox>

        <MDBox px={2} pb={2}>
          <MDBox
            sx={{
              border: "1px solid rgba(52, 71, 103, 0.08)",
              borderRadius: "0.75rem",
              overflow: "hidden",
            }}
          >
            <MDBox
              px={2}
              py={1.25}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "minmax(0, 1fr) auto 40px", md: "minmax(0, 1fr) 180px 40px" },
                gap: { xs: 1, md: 2 },
                backgroundColor: `${darkMode ? '#344767': '#f8f9fa'}`,
                borderBottom: "1px solid rgba(52, 71, 103, 0.08)",
              }}
            >
              <MDTypography variant="caption" fontWeight="bold" textTransform="uppercase">
                Categoria
              </MDTypography>
              <MDTypography variant="caption" fontWeight="bold" textTransform="uppercase">
                Limite mensal
              </MDTypography>
              <MDBox />
            </MDBox>

            {isLoading ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </MDBox>
            ) : limits.length > 0 ? (
              visibleLimits.map((item, index) => (
                <MDBox
                  key={item.category}
                  px={2}
                  py={1}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "minmax(0, 1fr) auto 40px", md: "minmax(0, 1fr) 180px 40px" },
                    gap: { xs: 1, md: 2 },
                    alignItems: "center",
                    borderBottom:
                      index === visibleLimits.length - 1 && (showAllLimits || hiddenLimitsCount === 0)
                        ? "none"
                        : "1px solid rgba(52, 71, 103, 0.08)",
                  }}
                >
                  <MDTypography variant="button" fontWeight="medium">
                    {formatRevenueExpenseCategory(item.category)}
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    {displayCurrency(item.maxValue)}
                  </MDTypography>
                  <Tooltip title="Editar limite">
                    <IconButton
                      aria-label={`Editar limite de ${formatRevenueExpenseCategory(item.category)}`}
                      size="small"
                      color="secondary"
                      onClick={() => openLimitModal(item.category, item.maxValue)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </MDBox>
              ))
            ) : (
              <MDBox px={2} py={4}>
                <MDTypography variant="button" color="text">
                  Nenhum limite configurado ainda. Clique em Adicionar limite para cadastrar o primeiro.
                </MDTypography>
              </MDBox>
            )}

            {!isLoading && hiddenLimitsCount > 0 && (
              <MDBox
                px={2}
                py={0.75}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  borderTop: showAllLimits ? "1px solid rgba(52, 71, 103, 0.08)" : "none",
                }}
              >
                <MDButton
                  variant="text"
                  color="info"
                  size="small"
                  onClick={() => setShowAllLimits((previousState) => !previousState)}
                  sx={{ gap: 0.5 }}
                >
                  {showAllLimits ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  {showAllLimits ? "Ver menos" : `Ver todas as ${limits.length} categorias`}
                </MDButton>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </Card>

      <RevenueExpenseCategoryLimitModal
        open={modalState.open}
        initialCategory={modalState.category}
        initialMaxValue={modalState.maxValue}
        hasExistingLimit={modalState.hasExistingLimit}
        lockCategory={modalState.hasExistingLimit}
        onClose={() => setModalState({ open: false, category: "", maxValue: "", hasExistingLimit: false })}
        onSaved={handleModalSaved}
        onDeleted={handleModalDeleted}
        openSnackBar={openSnackBar}
      />
    </>
  );
}

RevenueExpenseCategoryLimitsCard.defaultProps = {
  openSnackBar: undefined,
  valuesHidden: false,
};

RevenueExpenseCategoryLimitsCard.propTypes = {
  openSnackBar: PropTypes.func,
  valuesHidden: PropTypes.bool,
};

export default RevenueExpenseCategoryLimitsCard;
